import React, { useEffect } from 'react';
import { Box, Grid, CircularProgress, Alert, Typography } from '@mui/material';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../contexts/AuthContext';
import { useOrder } from '../hooks/useOrder';
import OrderHeader from '../components/orders/OrderHeader';
import ProductSelector from '../components/orders/ProductSelector';
import OrderItemsTable from '../components/orders/OrderItemsTable';
import OrderSidebar from '../components/orders/OrderSidebar';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

const NewOrder = ({ orderType: orderTypeProp }) => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const { id: editOrderId } = useParams();
  const [searchParams] = useSearchParams();
  const clientIdFromUrl = searchParams.get('clientId');
  const isEditMode = Boolean(editOrderId);

  const {
    order, orderItems, loading, error, success,
    restoreOrCreateOrder, fetchOrder,
    addItem, updateItem, removeItem, updateOrder,
    validateOrder,
    autoSaving
  } = useOrder();

  useEffect(() => {
    if (isEditMode) {
      // Edición de un pedido existente: se carga por id (ej: al volver desde la revisión).
      fetchOrder(editOrderId);
    } else {
      restoreOrCreateOrder(orderTypeProp);
    }
  }, [isEditMode, editOrderId, fetchOrder, restoreOrCreateOrder, orderTypeProp]);

  // Al crear, el canal viene de la ruta (público/mayorista).
  // Al editar, se deriva del pedido cargado.
  const orderType = isEditMode
    ? (order?.order_type === 'wholesale' ? 'business' : 'public')
    : orderTypeProp;

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  if (loading && !order) {
    return (
      <Box sx={{ display: 'flex', minHeight: '100vh' }}>
        <Sidebar onLogout={handleLogout} />
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column', gap: 2 }}>
          <CircularProgress />
          <Typography color="textSecondary">Cargando pedido...</Typography>
        </Box>
      </Box>
    );
  }

  if (error && !order) {
    return (
      <Box sx={{ display: 'flex', minHeight: '100vh' }}>
        <Sidebar onLogout={handleLogout} />
        <Box sx={{ flex: 1, p: 4 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', height: { md: '100vh' }, overflow: { md: 'hidden' }, bgcolor: '#f6f7f8' }}>
      <Sidebar onLogout={handleLogout} />
      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: { xs: 'visible', md: 'hidden' } }}>

        <OrderHeader
          order={order}
          client={order?.client}
          orderType={orderType}
          cancelLabel={isEditMode ? 'Volver al listado' : 'Cancelar'}
          onCancel={() => {
            const base = orderType === 'business' ? '/orders/business' : '/orders/public';
            // Al editar, cada cambio ya se guarda al instante: solo se vuelve al listado
            if (isEditMode) {
              navigate(`${base}/list`);
            } else if (window.confirm('¿Cancelar pedido?')) {
              window.location.href = base;
            }
          }}
        />

        {(error || success || autoSaving) && (
          <Box sx={{ px: 3, pt: 2 }}>
            {error && <Alert severity="error" onClose={() => { }}>{error}</Alert>}
            {success && <Alert severity="success" onClose={() => { }}>{success}</Alert>}
            {autoSaving && <Typography variant="caption" color="textSecondary">Guardando...</Typography>}
          </Box>
        )}

        <Box sx={{ flex: 1, minWidth: 0, overflow: { xs: 'visible', md: 'hidden' }, p: 2 }}>
          <Grid container spacing={2} sx={{ height: { xs: 'auto', md: '100%' } }}>
            {/* Left: Product Selector */}
            <Grid item xs={12} md={7} lg={8} sx={{ minWidth: 0, height: { xs: 'auto', md: '100%' }, overflow: { xs: 'visible', md: 'hidden' } }}>
              <Box sx={{ height: { xs: '70vh', md: '100%' }, bgcolor: 'white', borderRadius: 1, display: 'flex', flexDirection: 'column', border: '1px solid #e7edf3' }}>
                <ProductSelector
                  clientId={order?.client?.id || clientIdFromUrl}
                  channel={orderType === 'business' ? 'wholesale' : 'retail'}
                  onAddProduct={(product, qty) => addItem(product.id, qty, product.price_per_kg)}
                />
              </Box>
            </Grid>

            {/* Right: Items and Sidebar */}
            <Grid item xs={12} md={5} lg={4} sx={{ minWidth: 0, height: { xs: 'auto', md: '100%' }, overflow: { xs: 'visible', md: 'hidden' }, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ flex: 1, minHeight: { md: 240 }, overflowY: 'auto', bgcolor: 'white', borderRadius: 1, border: '1px solid #e7edf3' }}>
                <OrderItemsTable
                  items={orderItems}
                  onUpdateItem={updateItem}
                  onRemoveItem={removeItem}
                />
              </Box>
              <Box sx={{ flexShrink: 1, minHeight: 0, overflowY: 'auto', bgcolor: 'white', borderRadius: 1, border: '1px solid #e7edf3' }}>
                <OrderSidebar
                  order={order}
                  items={orderItems}
                  orderType={orderType}
                  onValidate={validateOrder}
                  deliveryDate={order?.delivery_date}
                  setDeliveryDate={(date) => updateOrder({ delivery_date: date })}
                  specialProduction={order?.special_production}
                  setSpecialProduction={(val) => updateOrder({ special_production: val })}
                />
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Box>
  );
};

export default NewOrder;
